/* eslint @typescript-eslint/strict-boolean-expressions: off */

import { DashboardPage } from "gui/dashboard/page";

import { useRouter } from "next/router";

import { GetServerSideProps } from "next";
import { fetchDashboardMetadata } from "lib/entries/entries";
import gql from "graphql-tag";
import { apolloCore } from "lib/apollo/apollo";
import {
  FindDashboardNameWithRedirectDocument,
  FindDashboardNameWithRedirectQuery,
  FindDashboardNameWithRedirectQueryVariables,
} from "lib/types/graphql";
import { mustEnvVar } from "lib/env/env";
import { sendEvent } from "api-shared/posthog/sendEvent";

interface Props {
  preview: boolean;
  timestamp: string;
  title?: string;
}

export default function UserDashboard(props: Props) {
  // Get variables from url /[slug]/[dashboard]. Safe to cast: They must both be string.
  const { slug, dashboard } = useRouter().query;
  const urlParams = {
    user: slug as string,
    dashboard: dashboard as string,
    apiKey: undefined,
  };

  return <DashboardPage {...props} urlParams={urlParams} />;
}

export const getServerSideProps: GetServerSideProps<Props> = async (ctx) => {
  const owner_handle = ctx.params?.slug as string;
  const dashboard = ctx.params?.dashboard as string;
  const result = await fetchDashboardMetadata(owner_handle, dashboard);

  // If there is no dashboard found with this owner + dashboard slug combo
  // then check to see if there exists a dashboard that has ben configured
  // to be redirected to using this owner + dashboard slug combo.
  if (!result) {
    const res = await apolloCore.query<
      FindDashboardNameWithRedirectQuery,
      FindDashboardNameWithRedirectQueryVariables
    >({
      query: FindDashboardNameWithRedirectDocument,
      variables: {
        redirect_from: `${owner_handle}/${dashboard}`,
      },
      context: { adminKey: mustEnvVar("DUNE_HSR_CORE_ADMIN_SECRET") },
      fetchPolicy: "no-cache",
    });
    const newDashboard = res.data.dashboards[0];
    const newOwner = newDashboard?.user?.name || newDashboard?.team?.handle;

    if (res.data.dashboards.length !== 0) {
      // We can use this data to check how this feature can be deprecated in the future
      sendEvent("dashboard_redirected", {
        distinct_id: "getServerSideProps", // Just need something here
        from: `/${owner_handle}/${dashboard}`,
        to: `/${owner_handle}/${dashboard}`,
      });
      return {
        redirect: {
          permanent: true,
          destination: `/${newOwner}/${newDashboard.slug}`,
        },
      };
    }
  }

  const title = result?.name;

  return {
    props: {
      preview: Boolean(ctx.preview),
      timestamp: new Date().toISOString(),
      title: title ?? "",
    },
  };
};

gql`
  query FindDashboardNameWithRedirect($redirect_from: String) {
    dashboards(where: { redirect_from: { _eq: $redirect_from } }) {
      id
      slug
      user {
        id
        name
      }
      team {
        id
        handle
      }
    }
  }
`;
